/**
 * PublicContract.jsx — public creator-facing signing page (no auth).
 * Route: /contract/:token
 */
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Loader2, Check, FileSignature } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";

export default function PublicContract() {
  const { token } = useParams();
  const [doc, setDoc] = useState(null);
  const [name, setName] = useState("");
  const [ack, setAck] = useState(false);
  const [signing, setSigning] = useState(false);

  useEffect(() => {
    api.get(`/public/influencer-contracts/${token}`)
      .then((r) => setDoc(r.data))
      .catch(() => setDoc({ error: "Contract not found or link expired." }));
  }, [token]);

  const sign = async () => {
    if (!name.trim()) { toast.error("Enter your full legal name"); return; }
    if (!ack) { toast.error("Tick the acknowledgement box"); return; }
    setSigning(true);
    try {
      const { data } = await api.post(`/public/influencer-contracts/${token}/sign`, {
        full_name: name.trim(), acknowledged: true,
      });
      toast.success("Signed — thank you!");
      setDoc({ ...doc, signed_at: data.signed_at, signed_by_name: name.trim(), status: "signed" });
    } catch (e) { toast.error(apiErrorMessage(e, "Sign failed")); }
    finally { setSigning(false); }
  };

  if (!doc) return <div className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">Loading…</div>;
  if (doc.error) return <div className="min-h-screen flex items-center justify-center text-sm text-rose-600">{doc.error}</div>;

  return (
    <div className="min-h-screen bg-background py-10 px-4" data-testid="public-contract-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="border-b border-border pb-4">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-muted-foreground">
            <FileSignature className="w-3.5 h-3.5" /> Influencer collaboration agreement
          </div>
          <h1 className="text-2xl font-bold mt-1">Contract for {doc.creator?.name || "creator"}</h1>
          <div className="text-xs text-muted-foreground mt-1">Issued {new Date(doc.issued_at).toLocaleString()}</div>
        </header>

        <article className="border border-border p-6 whitespace-pre-wrap text-sm leading-6 font-mono" data-testid="public-contract-body">
          {doc.rendered_body}
        </article>

        {doc.status === "signed" ? (
          <div className="border border-emerald-400 bg-emerald-50 p-4 text-sm" data-testid="public-contract-signed">
            <div className="flex items-center gap-2 font-semibold text-emerald-800">
              <Check className="w-4 h-4" /> Signed by {doc.signed_by_name}
            </div>
            <div className="text-xs text-emerald-700 mt-1">on {new Date(doc.signed_at).toLocaleString()}</div>
          </div>
        ) : (
          <div className="border border-border p-4 space-y-3">
            <div className="text-xs uppercase tracking-widest text-muted-foreground">Sign contract</div>
            <div>
              <label className="text-[10px] uppercase text-muted-foreground">Full legal name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} className="h-9 text-sm" data-testid="public-contract-name" />
            </div>
            <label className="flex items-start gap-2 text-xs">
              <input type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)} className="mt-0.5" data-testid="public-contract-ack" />
              I have read and accept the terms of this collaboration agreement.
            </label>
            <Button onClick={sign} disabled={signing} className="w-full" data-testid="public-contract-sign">
              {signing ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1.5" />}Sign contract
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
